import React, { useCallback, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useRoute, RouteProp, useNavigation, NavigationProp } from "@react-navigation/native";
import type { RootStackParamList } from "../navigation/navigation.types";
import ChatWindowContainer from "../containers/companion/ChatWindowContainer";
import { useGetCompanionChatByIdQuery } from "@axio/graphql/generated/graphql";
import CompanionLayout from "../layouts/CompanionLayout";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomButton from "../components/common/general/CustomButton/CustomButton";

function CompanionView() {
  const route = useRoute<RouteProp<RootStackParamList, "Companion Chat">>();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { chatId: routeChatId, initialMessage } = route.params || {};

  const {
    data: routeChatData,
    loading: routeChatLoading,
    refetch: refetchChat,
  } = useGetCompanionChatByIdQuery({
    skip: !routeChatId,
    variables: {
      id: routeChatId!,
    },
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  type RouteChatResult = { success?: boolean; message?: string | null; data?: unknown };
  const routeChatResult = routeChatData?.getCompanionChatById as RouteChatResult | undefined;
  const querySuccess = routeChatResult?.success !== false;

  const handleRetryRefetch = useCallback(() => {
    if (routeChatId && refetchChat) {
      refetchChat();
    }
  }, [routeChatId, refetchChat]);

  const resolvedChatId = useMemo(() => {
    if (!routeChatId) {
      return null;
    }
    if (routeChatLoading) {
      return routeChatId;
    }
    const chatData = routeChatResult && "data" in routeChatResult ? routeChatResult.data : routeChatResult;
    if (querySuccess && chatData) {
      return routeChatId;
    }
    return null;
  }, [routeChatId, routeChatLoading, querySuccess, routeChatResult]);

  const handleChatIdChange = useCallback(
    (newChatId: string) => {
      navigation.setParams({ chatId: newChatId });
    },
    [navigation]
  );

  const handleInitialMessageConsumed = useCallback(() => {
    navigation.setParams({ initialMessage: undefined });
  }, [navigation]);

  const isLoadingChatId = Boolean(routeChatId) && routeChatLoading;
  const showLoadError = Boolean(routeChatId && querySuccess === false);

  if (showLoadError) {
    return (
      <CompanionLayout>
        <View style={styles.errorContainer}>
          <CustomText
            text={
              (routeChatResult && "message" in routeChatResult && routeChatResult.message) ??
              "We were not able to load your chat data."
            }
            size="m"
            useTranslationText={false}
            style={styles.errorMessage}
          />
          <CustomButton
            text="Retry"
            onPress={handleRetryRefetch}
            useTranslationText={false}
            style={styles.retryButton}
          />
        </View>
      </CompanionLayout>
    );
  }

  return (
    <CompanionLayout>
      <ChatWindowContainer
        chatId={resolvedChatId}
        onChatIdChange={handleChatIdChange}
        isLoadingChatId={isLoadingChatId}
        initialMessage={initialMessage}
        onInitialMessageConsumed={handleInitialMessageConsumed}
      />
    </CompanionLayout>
  );
}

const styles = StyleSheet.create({
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  errorMessage: {
    textAlign: "center",
    marginBottom: 16,
  },
  retryButton: {
    minWidth: 120,
  },
});

export default CompanionView;
